import React, { useMemo } from 'react';

import { Badge, Group, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { IconAlarm, IconCalendar, IconHourglassEmpty, IconRefresh, IconTicket } from '@tabler/icons-react';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import { PackageRetrieve, SalesPortalPackageRetrieve } from 'types';
import { getAttendanceRuleSummary, getEndConditions } from 'utils/billing.helpers';

import PackageTypeDescription from './PackageTypeDescription';
import { getPackageSalesRemaining } from './SalesPortalHomePagePackages';

interface IPackageSummaryProps {
  pckg: SalesPortalPackageRetrieve | PackageRetrieve;
  hasBackButton?: boolean;
}

const PackageSummary = ({ pckg }: IPackageSummaryProps): JSX.Element => {
  const theme = useMantineTheme();
  const { dateFormat } = useLocalization();

  const salesPackageRemaining = useMemo(() => {
    return getPackageSalesRemaining(Number(pckg.numValidSales), pckg.maxSalesAllowed || null);
  }, [pckg.maxSalesAllowed, pckg.numValidSales]);

  const includesSuccessor = pckg.successorPackage && pckg.successorBillingPlan;
  const endConditions = getEndConditions(pckg.attendanceRules, pckg.type);

  return (
    <Stack spacing='lg'>
      <Group position='apart' noWrap>
        <Title order={3} truncate>
          {pckg.name}
        </Title>
        <Badge color='gray' variant='outline' size='sm'>
          {pckg.category ? pckg.category : 'Uncategorized'}
        </Badge>
      </Group>

      <PackageTypeDescription type={pckg.type} size='sm' />

      <Group>
        {salesPackageRemaining && salesPackageRemaining}

        {pckg.startDate && (
          <Group noWrap spacing='xs'>
            <IconCalendar size={theme.fontSizes.xl} />
            <Text size='md'>{`Starts on ${dayjs(pckg.startDate).format(dateFormat)}`}</Text>
          </Group>
        )}

        {pckg.autoRenew && (
          <Group noWrap spacing='xs'>
            <IconRefresh color={theme.colors.green[5]} size={theme.fontSizes.xl} />
            <Text size='md'>Auto-renew</Text>
          </Group>
        )}

        {includesSuccessor && (
          <Group noWrap spacing='xs'>
            <IconRefresh color={theme.colors.green[5]} size={theme.fontSizes.xl} />
            <Text size='md'>Renews to a different package</Text>
          </Group>
        )}
      </Group>

      {pckg.description && (
        <Text size='md' color='dark.4' sx={{ wordWrap: 'break-word' }}>
          {pckg.description}
        </Text>
      )}

      <Stack spacing='xs'>
        {endConditions && (
          <Group spacing='xs' noWrap>
            <IconAlarm size={theme.fontSizes.xl} />
            <Text weight='600'>{endConditions}</Text>
          </Group>
        )}
        {getAttendanceRuleSummary(pckg.attendanceRules).map((ar, i) => (
          <Stack key={i} spacing={2}>
            <Text size='lg' weight='600'>
              {ar.name}
            </Text>
            {pckg.type === 'CREDIT_BASED' && (
              <Group spacing='xs'>
                <IconTicket size={theme.fontSizes.xl} />
                <Text>{ar.attendance}</Text>
              </Group>
            )}
            {pckg.type === 'TIME_BASED' && (
              <Group spacing='xs'>
                <IconHourglassEmpty size={theme.fontSizes.xl} />
                <Text>
                  {` ${ar.attendance} for ${ar.validity} ${
                    ar.delay === '-' ? '' : ` starting after ${ar.delay}`
                  }`}
                </Text>
              </Group>
            )}
            {pckg.type === 'TIME_OR_CREDIT_BASED' && (
              <Group spacing='xs'>
                <IconHourglassEmpty size={theme.fontSizes.xl} />
                <Stack spacing={0}>
                  <Text>{ar.summary}</Text>
                  {ar.delay !== '-' && <Text size='sm' color='dark.3'>{`Starts after ${ar.delay}`}</Text>}
                </Stack>
              </Group>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

export default PackageSummary;
